import React, { useMemo, useState, useEffect, useCallback } from 'react';
import './LeadList.css';
import Table from '../../common/Table';
import TextInput from '../../common/TextInput';
import Button from '../../common/Button';
import LoadingSpinner from '../../common/LoadingSpinner';
import Alert from '../../common/Alert';
import { createLeadColumns } from './leadColumns';
import { leadApi } from '../../../services/apis/configuredApis';
import LeadDetailModal from './LeadDetailModal';

function LeadList({ onError, onSuccess }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [leadMagnetFilter, setLeadMagnetFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const pageSize = 20;

  const loadLeads = useCallback(async (search = null, email = null, country = null, leadMagnetId = null, page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const offset = (page - 1) * pageSize;
      
      const response = await leadApi.leadListGet({
        search: search || null,
        leadMagnetId: leadMagnetId || null,
        email: email || null,
        country: country || null,
        emailDomain: null,
        limit: pageSize,
        offset: offset,
        orderBy: 'created_at',
        orderDir: 'DESC',
      });
      
      // Axios wraps the response, so the actual data is in response.data
      const responseData = response?.data || response;
      const leadsList = responseData?.leads || [];
      const count = responseData?.totalCount || 0;
      
      setLeads(leadsList);
      setTotalCount(count);
      
      if (onSuccess && leadsList.length > 0) {
        onSuccess(null); // Clear any previous success messages
      }
    } catch (err) {
      console.error('Error loading leads:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to load leads';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
      setLeads([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [onError, onSuccess]);

  const handleView = useCallback((lead) => {
    setSelectedLead(lead);
    setShowDetailModal(true);
  }, []);

  const handleEdit = useCallback((lead) => {
    setSelectedLead(lead);
    setShowEditModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowDetailModal(false);
    setShowEditModal(false);
    setSelectedLead(null);
  }, []);

  const handleUpdateSuccess = useCallback(() => {
    setSuccess('Lead updated successfully');
    setShowEditModal(false);
    setSelectedLead(null);
    // Reload the list
    loadLeads(
      searchTerm.trim() || null,
      emailFilter.trim() || null,
      countryFilter.trim() || null,
      leadMagnetFilter.trim() || null,
      currentPage
    );
    setTimeout(() => setSuccess(null), 3000);
  }, [searchTerm, emailFilter, countryFilter, leadMagnetFilter, currentPage, loadLeads]);

  const columns = useMemo(
    () => createLeadColumns({ onView: handleView, onEdit: handleEdit }),
    [handleView, handleEdit]
  );

  // Load data on mount and when filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadLeads(
        searchTerm.trim() || null,
        emailFilter.trim() || null,
        countryFilter.trim() || null,
        leadMagnetFilter.trim() || null,
        currentPage
      );
    }, searchTerm || emailFilter || countryFilter ? 300 : 0); // Debounce search by 300ms

    return () => clearTimeout(timeoutId);
  }, [searchTerm, emailFilter, countryFilter, leadMagnetFilter, currentPage, loadLeads]);

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && leads.length === 0) {
    return <LoadingSpinner text="Loading leads..." />
  }

  return (
    <div className="lead-list">
      {error && (
        <Alert
          variant="error"
          onClose={() => {
            setError(null);
            if (onError) onError(null);
          }}
          className="lead-list__alert"
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          variant="success"
          onClose={() => setSuccess(null)}
          className="lead-list__alert"
        >
          {success}
        </Alert>
      )}

      {/* Filters */}
      <div className="lead-list__filters">
        <div className="lead-list__filter-group">
          <TextInput
            placeholder="Search by name, email, company..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page on new search
            }}
            className="lead-list__filter-input"
          />
        </div>
        <div className="lead-list__filter-group">
          <TextInput
            placeholder="Filter by email..."
            value={emailFilter}
            onChange={(e) => {
              setEmailFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="lead-list__filter-input"
          />
        </div>
        <div className="lead-list__filter-group">
          <TextInput
            placeholder="Filter by country..."
            value={countryFilter}
            onChange={(e) => {
              setCountryFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="lead-list__filter-input"
          />
        </div>
        {(searchTerm || emailFilter || countryFilter) && (
          <Button
            variant="ghost"
            className="lead-list__clear-filters"
            onClick={() => {
              setSearchTerm('');
              setEmailFilter('');
              setCountryFilter('');
              setLeadMagnetFilter('');
              setCurrentPage(1);
            }}
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="lead-list__table-container">
        <Table
          columns={columns}
          data={leads}
          keyExtractor={(row) => row.id}
          emptyMessage="No leads found. Leads will appear here when users submit forms on your lead magnets."
        />
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="lead-list__pagination">
          <Button
            variant="secondary"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="lead-list__pagination-btn"
          >
            Previous
          </Button>
          <span className="lead-list__pagination-info">
            Page {currentPage} of {totalPages} ({totalCount} total)
          </span>
          <Button
            variant="secondary"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="lead-list__pagination-btn"
          >
            Next
          </Button>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedLead && (
        <LeadDetailModal
          show={true}
          lead={selectedLead}
          onClose={handleCloseModal}
          onEdit={() => {
            setShowDetailModal(false);
            setShowEditModal(true);
          }}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedLead && (
        <LeadDetailModal
          show={true}
          lead={selectedLead}
          mode="edit"
          onClose={handleCloseModal}
          onSuccess={handleUpdateSuccess}
        />
      )}
    </div>
  );
}

export default LeadList;











